import { shuffle } from '../utilities'
import i18n from '../../../utilities/translation'
import { refreshVisibleAds } from '../utilities/ad_behaviors'
import ga4RevealWidgetClickHandler from '../ga4_support/blocks/reveal_widget'
import GA4Handler from '../ga4_support/GA4Handler'

const language = document.documentElement.lang

const BASE_CLICK_TEXT = i18n('Fortune Cookie: Press Enter to reveal your fortune.', language)
const BASE_REFRESH_TEXT = i18n('Click to reset the fortune cookie.', language)

const initFortuneCookie = (widgetContainer: HTMLElement, items: string[]) => {
  let displayCount = 0
  let unseenFortunes = shuffle(items)

  let isRevealed = false

  GA4Handler.impressionHandleEvent(widgetContainer as HTMLElement)

  const widgetInnerContainer = widgetContainer.querySelector('.reveal-widget__inner-container')

  // Init template
  const fortuneCookieItem = widgetContainer.querySelector('.fortune-cookie')
  const activeRegion: any = widgetContainer.querySelector('.reveal-widget__dialog-region')
  const fortuneCookieMessageContainer = widgetContainer.querySelector('.reveal-widget__message')
  const actionButtons: any = widgetContainer.querySelector('.block-item__actions')
  const footer: any = widgetContainer.querySelector('.reveal-widget__footer')

  //set default gtm data attr fortune text when page loads
  widgetContainer.setAttribute('data-gtm-share-text', 'fortune cookie not revealed')

  const hideActionButtons = () => {
    actionButtons.setAttribute('tabindex', '-1')
    actionButtons.setAttribute('aria-hidden', 'true')
    actionButtons.classList.add('hidden')
  }

  const showActionButtons = () => {
    actionButtons.setAttribute('tabindex', '0')
    actionButtons.removeAttribute('aria-hidden')
    actionButtons.classList.remove('hidden')
  }

  const showMessage = () => {
    fortuneCookieItem.classList.remove('reveal-widget--hide-message')
    fortuneCookieItem.classList.add('reveal-widget--show-message')

    footer.classList.remove('reveal-widget--footer-disappear')
    footer.classList.add('reveal-widget--footer-appear')
    widgetContainer.setAttribute('data-gtm-feature', 'refresh')
    showActionButtons()
  }

  const hideMessage = () => {
    fortuneCookieItem.classList.remove('reveal-widget--show-message')
    fortuneCookieItem.classList.add('reveal-widget--hide-message')

    footer.classList.remove('reveal-widget--footer-appear')
    footer.classList.add('reveal-widget--footer-disappear')
    widgetContainer.setAttribute('data-gtm-feature', 'show')
    widgetContainer.setAttribute('data-gtm-share-text', 'fortune cookie not revealed')
    hideActionButtons()
  }

  // Fortune cookie message container
  const updateFortuneCookieMessage = (message = '') => {
    fortuneCookieMessageContainer.innerHTML = message
    widgetContainer.setAttribute('data-mp-share-fortune-text', message)
    widgetContainer.setAttribute('data-gtm-share-text', message)
  }

  // Why the singleton method call? The problem it solves, at least for now, is that
  // a live aria region that describes the button will announce on page load. No good.
  // But interacting with the element will cause text changes we need to broadcast without changing focus,
  // so the selected element needs to either be quiet or be described by a live region. Leaving the base aria-label
  // on meant the button text got repeated all the time. It was also invalid half the time because the 'button'
  // is really an overloaded one and aria labels aren't meant to be toggled.
  // So we remove the base aria label the first time and start labelling by a hidden live region that plays the fortune
  // and supplmentary text indicating what interacting with the button again will do. This is a failure caused by overloading
  // interactive elements.
  let isClickedOnce = false
  const refreshContent = () => {
    if (!isClickedOnce) {
      widgetInnerContainer.removeAttribute('aria-label')
      widgetInnerContainer.setAttribute('aria-labelledby', 'fortune-cookie__dialog-region')
      isClickedOnce = true
    }
    if (!isRevealed) {
      // Reset unseen fortunes if there are none left
      if (!unseenFortunes.length) {
        unseenFortunes = shuffle(items)
      }

      // Gets and removes last fortune to not repeat the same fortunes
      const newFortune = unseenFortunes.pop()
      updateFortuneCookieMessage(newFortune)
      activeRegion.innerText = newFortune + '\n\n' + BASE_REFRESH_TEXT
      showMessage()
      ga4RevealWidgetClickHandler(
        widgetContainer,
        'editorial',
        'show',
        newFortune,
        newFortune,
        ++displayCount
      )
      refreshVisibleAds()
    } else {
      activeRegion.innerText = BASE_CLICK_TEXT
      updateFortuneCookieMessage()
      hideMessage()
      ga4RevealWidgetClickHandler(widgetContainer, 'editorial', 'refresh', '', '')
    }
    isRevealed = !isRevealed
  }

  hideActionButtons()
  widgetInnerContainer.addEventListener('click', refreshContent)
  widgetInnerContainer.addEventListener('keyup', (e: any) => {
    if (e.type !== 'keyup') {
      return
    }
    if (['Enter', ' '].includes((e as any).key)) {
      refreshContent()
    }
  })
}

export default initFortuneCookie
